import { FormEvent, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { useUpdateClientMutation } from "store/api/clientApi"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconClose } from "assets/svg/icon-close.svg"

import getErrorMessage from "utils/getErrorMessage"
import { customerDetailsSchema } from "utils/validationShemas"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { InputNew } from "components/form/InputNew"
import { SelectStatic } from "components/form/SelectStatic"
import { Textarea } from "components/form/Textarea"

import { useCustomerDashboard } from "../CustomerDashboardContext"
import {
  CloseButton,
  FieldItem,
  FieldLabel,
  FieldRow,
  FieldWrapper,
  MobileAppContainer,
  MobileAppLabel,
  MobileAppValue,
  ReadOnlyValue,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTitle,
  StyledButtonNew,
  StyledInputNew,
  StyledInputPhone,
} from "./styled"

interface Props {
  isOpen: boolean
  customer: ApiTypes.Model.ClientDevices | null
  onClose: () => void
  onUpdateSuccess?: () => void
}

// Extended type to handle additional fields
type CustomerData = ApiTypes.Model.ClientDevices & {
  notes?: string
  app_activated?: boolean
}

export const CustomerDetailsSidebar = ({ isOpen, customer, onClose, onUpdateSuccess }: Props) => {
  const navigate = useNavigate()
  const { setHighlightTransfer } = useCustomerDashboard()
  const [updateClient, { isLoading }] = useUpdateClientMutation()
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    notes: "",
    membership: [] as UITypes.Option[],
  })
  const [errors, setErrors] = useState({
    email: "",
    phone_number: "",
  })

  useEffect(() => {
    if (customer) {
      const customerData = customer as CustomerData
      setFormData({
        full_name: customerData.full_name || "",
        email: customerData.email || "",
        phone_number: customerData.phone_number || "",
        notes: customerData.notes || "",
        membership: customerData.membership
          ? [
              {
                value: customerData.membership,
                label: customerData.membership.charAt(0).toUpperCase() + customerData.membership.slice(1),
              },
            ]
          : [{ value: "no", label: "No" }],
      })
      setErrors({
        email: "",
        phone_number: "",
      })
    }
  }, [isOpen, customer])

  const handleInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setFormData(prev => ({ ...prev, [name]: value }))
    if (name === "email" && errors.email) {
      setErrors(prev => ({ ...prev, email: "" }))
    }
  }

  const handleTextareaChange = (event: FormEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.currentTarget
    if (name === "notes" && value.length > 200) {
      return
    }
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, phone_number: value }))
    if (errors.phone_number) {
      setErrors(prev => ({ ...prev, phone_number: "" }))
    }
  }

  const handleSave = async () => {
    if (!customer) return

    const validationData = {
      email: formData.email,
      phone_number: formData.phone_number,
    }

    const result = customerDetailsSchema.validate(validationData, { abortEarly: false })

    if (result.error) {
      const newErrors = {
        email: "",
        phone_number: "",
      }

      result.error.details.forEach(detail => {
        const field = detail.path[0] as keyof typeof newErrors
        if (field in newErrors) {
          newErrors[field] = detail.message
        }
      })

      setErrors(newErrors)
      return
    }

    setErrors({
      email: "",
      phone_number: "",
    })

    try {
      const customerData = customer as CustomerData
      const customerId = customerData.id || 0

      const result = await updateClient({
        id: customerId,
        body: {
          client: {
            full_name: formData.full_name,
            email: formData.email,
            tel: formData.phone_number,
            membership: (formData.membership[0]?.value as string) || "no",
            notes: formData.notes,
          },
          devices: [],
        } as unknown as ApiTypes.Req.ClientDevices,
      })

      if (result.error) {
        if (
          "data" in result.error &&
          result.error.data &&
          typeof result.error.data === "object" &&
          "errors" in result.error.data
        ) {
          const backendErrors = (result.error.data as { errors?: Record<string, string | string[]> }).errors

          if (backendErrors) {
            if (backendErrors["client.email"]) {
              const emailError = Array.isArray(backendErrors["client.email"])
                ? backendErrors["client.email"][0]
                : backendErrors["client.email"]
              setErrors(prev => ({ ...prev, email: emailError || "" }))

              const customerId = (customer as CustomerData).id
              if (customerId) {
                navigate(`/customers/${customerId}/details`)
                setHighlightTransfer(true)
              }
              return
            }

            const errorMessage = getErrorMessage(result.error)
            if (errorMessage) {
              toast.error(errorMessage)
              return
            }
          }
        }

        const errorMessage = getErrorMessage(result.error)
        if (errorMessage) {
          toast.error(errorMessage)
        } else {
          toast.error("Failed to update customer details")
        }
        return
      }

      toast.success("Customer details updated successfully")
      setErrors({
        email: "",
        phone_number: "",
      })
      onUpdateSuccess?.()
    } catch {
      toast.error("Failed to update customer details")
    }
  }

  if (!customer) return null

  const customerData = customer as CustomerData
  const appActivated = customerData.app_activated === true
  const customerId = customerData.id
  const devicesCount = customerData.devices?.length || 0

  return (
    <Sidebar isOpen={isOpen}>
      <SidebarHeader>
        <SidebarTitle>Customer Details</SidebarTitle>
        <CloseButton onClick={onClose}>
          <IconClose />
        </CloseButton>
      </SidebarHeader>
      <SidebarContent>
        <section>
          <ModalSectionTitle title="Customer Information" />
          <FieldRow>
            <FieldItem>
              <FieldWrapper>
                <FieldLabel>Customer ID</FieldLabel>
                <ReadOnlyValue>{customerId || "-"}</ReadOnlyValue>
              </FieldWrapper>
            </FieldItem>
            <FieldItem>
              <FieldWrapper>
                <FieldLabel>Devices</FieldLabel>
                <ReadOnlyValue>{devicesCount}</ReadOnlyValue>
              </FieldWrapper>
            </FieldItem>
          </FieldRow>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Full Name</FieldLabel>
              <InputNew
                placeholder="Enter Full Name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                errorMessage=""
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Email</FieldLabel>
              <StyledInputNew
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                errorMessage={errors.email}
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Phone Number</FieldLabel>
              <StyledInputPhone
                placeholder="Enter phone number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handlePhoneChange}
                errorMessage={errors.phone_number}
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Membership</FieldLabel>
              <SelectStatic
                placeholder="Select Membership"
                values={formData.membership}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
                onChange={value => setFormData(prev => ({ ...prev, membership: value }))}
                errorMessage=""
              />
            </FieldWrapper>
          </FieldItem>
        </section>
        <section>
          <ModalSectionTitle title="Mobile App" />
          <FieldRow>
            <FieldItem>
              <MobileAppContainer>
                <MobileAppLabel>Mobile App Installed and activated</MobileAppLabel>
                <MobileAppValue>{appActivated ? "Yes" : "No"}</MobileAppValue>
              </MobileAppContainer>
            </FieldItem>
          </FieldRow>
        </section>
        <section>
          <ModalSectionTitle title="Notes" />
          <FieldRow>
            <FieldItem>
              <FieldWrapper>
                <Textarea
                  rows={2}
                  maxLength={200}
                  placeholder="Enter notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleTextareaChange}
                  skipLabel
                  showCharCount
                  errorMessage=""
                />
              </FieldWrapper>
            </FieldItem>
          </FieldRow>
        </section>
      </SidebarContent>
      <SidebarFooter>
        <StyledButtonNew onClick={handleSave} disabled={isLoading} color="blue" text="Save" />
      </SidebarFooter>
    </Sidebar>
  )
}
